import { paymentWayOf } from '@console/shared-kernel';
import { ChevronLeft, ChevronRight, FileWarning, Plus, Receipt, Trash2 } from 'lucide-react';
import { type ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation, useNavigate, useParams } from 'react-router';
import { toast } from 'sonner';
import { StateMessage } from '@/admin-area/ui/StateMessage';
import { Button } from '@/components/ui/button';
import { scheduleUndoable } from '@/admin-area/ui/undoable';
import { RowActionsMenu } from '@/components/ui/row-actions-menu';
import { apiClient } from '@/lib/api-client';
import { Skeleton, SkeletonRegion } from '@/components/ui/skeleton';
import { formatAmount } from '../money';
import {
  categoryLabel,
  DocumentBadge,
  formatLongDate,
  NotReimbursableBadge,
  StatusBadge,
  UntraceableBadge,
} from '../list/expense-labels';
import { hideExpense, showExpense } from '../list/hidden-expenses';
import { expenseName } from '../list/row-title';
import { formatKm, formatRate } from '../mileage/calc';
import { isAllowanceLine } from '../flat-rate/category';
import { type CardNavigation, neighbours, readCardNavigation } from './card-navigation';
import { DocumentPreview } from './DocumentPreview';
import { classOf } from '../internal-class';
import { ClassControl } from './ClassControl';
import { ExpenseHistory } from './ExpenseHistory';
import { StatusChangePanel, type StatusChangeOutcome } from './StatusChangePanel';
import { type Expense, useExpense } from './use-expense';

/**
 * One expense, read only (design §1.5, the board's «scheda»): the document on the left, the facts
 * on the right, the state and who it belongs to in the head. The actions follow the expense's own
 * `can`: «Modifica» and «Aggiungi il documento» open the edit page (built by capture), «Cambia
 * stato» opens the manual-state panel, «Elimina la bozza» sits in the «···» menu. Opened from the
 * list, ‹ › walk the list's rows in its order (router state, no API).
 */
export function ExpenseDetailPage() {
  const { t } = useTranslation('expenses');
  const { expenseId = '' } = useParams();
  const { state, reload, replace } = useExpense(expenseId);
  const location = useLocation();
  const navigation = readCardNavigation(location.state);
  const listPath = navigation?.from ?? '/expenses';

  return (
    <section className="flex flex-col gap-4">
      <div className="flex items-center justify-between gap-3">
        <Link
          to={listPath}
          className="text-muted-foreground hover:text-foreground inline-flex w-fit items-center gap-1 text-sm"
        >
          <ChevronLeft aria-hidden="true" className="size-4" />
          {t('detail.back')}
        </Link>
        <PreviousNext navigation={navigation} expenseId={expenseId} />
      </div>
      <DetailBody
        state={state}
        listPath={listPath}
        navigation={navigation}
        onReload={reload}
        onReplace={replace}
      />
    </section>
  );
}

function DetailBody({
  state,
  listPath,
  navigation,
  onReload,
  onReplace,
}: {
  readonly state: ReturnType<typeof useExpense>['state'];
  readonly listPath: string;
  readonly navigation: CardNavigation | null;
  readonly onReload: () => void;
  readonly onReplace: (expense: Expense) => void;
}) {
  const { t } = useTranslation('expenses');
  if (state.status === 'loading') {
    return (
      <SkeletonRegion label={t('detail.loading')} className="flex flex-col gap-3">
        <Skeleton className="h-10 w-2/3" />
        <Skeleton className="h-64 w-full" />
      </SkeletonRegion>
    );
  }
  if (state.status === 'not-found') {
    return (
      <StateMessage
        icon={Receipt}
        title={t('detail.notFound.title')}
        text={t('detail.notFound.text')}
        action={
          <Button asChild variant="outline">
            <Link to={listPath}>{t('detail.back')}</Link>
          </Button>
        }
      />
    );
  }
  if (state.status === 'error') {
    return (
      <StateMessage
        role="alert"
        tone="error"
        icon={Receipt}
        title={t('detail.loadError')}
        action={
          <Button variant="outline" onClick={onReload}>
            {t('detail.retry')}
          </Button>
        }
      />
    );
  }
  return (
    <ExpenseCard
      expense={state.expense}
      listPath={listPath}
      navigation={navigation}
      onReload={onReload}
      onReplace={onReplace}
    />
  );
}

/** ‹ › to the previous and next expense of the list the card was opened from; off at the ends. */
function PreviousNext({
  navigation,
  expenseId,
}: {
  readonly navigation: CardNavigation | null;
  readonly expenseId: string;
}) {
  const { t } = useTranslation('expenses');
  const around = neighbours(navigation, expenseId);
  if (around === null || navigation === null) return null;
  const step = (id: string | null, label: string, icon: ReactNode) => {
    if (id === null) {
      return (
        <Button variant="outline" size="icon" disabled aria-label={label}>
          {icon}
        </Button>
      );
    }
    return (
      <Button asChild variant="outline" size="icon">
        <Link to={`/expenses/${encodeURIComponent(id)}`} state={navigation} aria-label={label}>
          {icon}
        </Link>
      </Button>
    );
  };
  return (
    <nav aria-label={t('detail.walk.label')} className="flex items-center gap-1.5">
      {step(around.previous, t('detail.walk.previous'), <ChevronLeft aria-hidden="true" />)}
      {step(around.next, t('detail.walk.next'), <ChevronRight aria-hidden="true" />)}
    </nav>
  );
}

/*
 * «Elimina la bozza», the board's flow: no question first. The card goes back to the list, which
 * leaves the draft out at once; the toast offers «Annulla», and the `DELETE` (a soft delete,
 * decision 131) is sent only when the window expires without it. «Annulla» or a refused write
 * brings the row back; a refusal says why.
 */
function useDeleteDraft(
  id: string,
  listPath: string,
  navigation: CardNavigation | null,
): () => void {
  const { t } = useTranslation('expenses');
  const navigate = useNavigate();
  return () => {
    hideExpense(id);
    void navigate(listPath, navigation === null ? undefined : { state: navigation });
    scheduleUndoable({
      id: `expense-delete-${id}`,
      message: t('detail.delete.done'),
      undoLabel: t('detail.delete.undo'),
      countdown: (seconds) => t('detail.delete.countdown', { seconds }),
      run: async () => {
        const { response } = await apiClient.DELETE('/v1/expenses/{expenseId}', {
          params: { path: { expenseId: id } },
        });
        if (response.status === 409) throw new NotADraft();
        if (!response.ok) throw new Error(`DELETE ${String(response.status)}`);
      },
    }).then(
      (outcome) => {
        if (outcome === 'undone') showExpense(id);
      },
      (error: unknown) => {
        showExpense(id);
        if (error instanceof NotADraft) toast.warning(t('detail.delete.notADraft'));
        else toast.error(t('detail.delete.failed'));
      },
    );
  };
}

function ExpenseCard({
  expense,
  listPath,
  navigation,
  onReload,
  onReplace,
}: {
  readonly expense: Expense;
  readonly listPath: string;
  readonly navigation: CardNavigation | null;
  readonly onReload: () => void;
  readonly onReplace: (expense: Expense) => void;
}) {
  const { t } = useTranslation('expenses');
  const [changingStatus, setChangingStatus] = useState(false);
  const isTrip = expense.category === 'mileage-reimbursement' && Boolean(expense.km);
  // An allowance line is never edited (`CATEGORY_RESERVED`): only Gestione or an approver takes it
  // out of its note, with a reason (decision 203).
  const allowance = isAllowanceLine(expense);
  const editPath = isTrip
    ? `/expenses/trips/${encodeURIComponent(expense.id)}/edit`
    : `/expenses/${encodeURIComponent(expense.id)}/edit`;
  const title = `${expenseName(expense, t)} · ${formatAmount(expense.total ?? '')}`;
  const onDelete = useDeleteDraft(expense.id, listPath, navigation);
  function onStatusDone(outcome: StatusChangeOutcome) {
    setChangingStatus(false);
    if (outcome.kind === 'conflict') {
      toast.warning(t('detail.statusChange.conflict'));
      onReload();
      return;
    }
    onReplace(outcome.expense);
    toast.success(t('detail.statusChange.done'));
  }

  return (
    <>
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="flex min-w-0 flex-col gap-1">
          <ExpenseHead expense={expense} title={title} />
          {allowance ? <AllowanceHint expense={expense} /> : null}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {expense.can.setStatus ? (
            <Button variant="outline" onClick={() => setChangingStatus(true)}>
              {t('detail.statusChange.open')}
            </Button>
          ) : null}
          {expense.can.edit && !allowance ? (
            <Button asChild variant="outline">
              <Link to={editPath}>{t('detail.edit')}</Link>
            </Button>
          ) : null}
          {expense.can.delete ? (
            <RowActionsMenu
              label={t('detail.moreActions')}
              actions={[
                {
                  id: 'delete',
                  label: t('detail.delete.open'),
                  icon: Trash2,
                  tone: 'destructive',
                  onSelect: onDelete,
                },
              ]}
            />
          ) : null}
        </div>
      </div>
      <div className="grid gap-6 @min-[900px]/shell:grid-cols-[minmax(0,1.1fr)_minmax(0,1fr)]">
        <DocumentPane expense={expense} editPath={editPath} />
        <section aria-label={t('detail.factsLabel')} className="flex flex-col gap-4">
          <Facts expense={expense} />
          <TaxLines lines={expense.taxLines} />
          <ExpenseClass expense={expense} onReplace={onReplace} />
          <ExpenseHistory expenseId={expense.id} revision={`${expense.version}${expense.status}`} />
        </section>
      </div>
      {expense.can.setStatus ? (
        <StatusChangePanel
          open={changingStatus}
          onOpenChange={setChangingStatus}
          expense={expense}
          title={title}
          onDone={onStatusDone}
        />
      ) : null}
    </>
  );
}

/** #915 C4: the class, when the view carries it (Gestione and the note's approvers). */
function ExpenseClass({
  expense,
  onReplace,
}: {
  readonly expense: Expense;
  readonly onReplace: (expense: Expense) => void;
}) {
  const value = classOf(expense);
  if (value === undefined) return null;
  return <ClassControl<Expense> expenseId={expense.id} value={value} onSaved={onReplace} />;
}

function ExpenseHead({ expense, title }: { readonly expense: Expense; readonly title: string }) {
  const { t } = useTranslation('expenses');
  const createdForSomeoneElse =
    expense.createdBy.userId !== undefined &&
    expense.owner.userId !== undefined &&
    expense.createdBy.userId !== expense.owner.userId;
  return (
    <div className="flex min-w-0 flex-col gap-1.5">
      <span className="text-muted-foreground text-xs font-semibold tracking-wide uppercase">
        {t('detail.title')}
      </span>
      <h2 className="screen-title">{title}</h2>
      <div className="flex flex-wrap items-center gap-2 text-sm">
        <StatusBadge status={expense.status} />
        <UntraceableBadge traceable={expense.traceablePayment} />
        <NotReimbursableBadge
          traceable={expense.traceablePayment}
          reimbursable={expense.reimbursable}
        />
        <span className="text-muted-foreground">
          {createdForSomeoneElse
            ? t('detail.createdFor', {
                creator: expense.createdBy.displayName ?? '',
                owner: expense.owner.displayName ?? '',
              })
            : t('detail.ownedBy', { owner: expense.owner.displayName ?? '' })}
        </span>
      </div>
    </div>
  );
}

function PaymentNote({ expense }: { readonly expense: Expense }) {
  const { t } = useTranslation('expenses');
  if (expense.traceablePayment === false) {
    return (
      <span className="text-warning block text-xs">{t('detail.paymentNote.untraceable')}</span>
    );
  }
  if (expense.reimbursable === false) {
    return (
      <span className="text-muted-foreground block text-xs">
        {t('detail.paymentNote.companyPaid')}
      </span>
    );
  }
  return null;
}

/**
 * An allowance line says it is not edited, and links its note, where «Togli» takes it away (U1,
 * U2). The view sends `reportId` and `reportNumber`; the generated R1 schema does not list them.
 */
function AllowanceHint({ expense }: { readonly expense: Expense }) {
  const { t } = useTranslation('expenses');
  const { reportId, reportNumber } = expense as Expense & {
    readonly reportId?: string | null;
    readonly reportNumber?: string | null;
  };
  return (
    <p className="text-muted-foreground text-sm">
      {t('flatRate.detailHint')}
      {reportId ? (
        <>
          {' '}
          <Link
            to={`/expenses/reports/${encodeURIComponent(reportId)}`}
            className="text-foreground font-semibold hover:underline"
          >
            {reportNumber
              ? t('flatRate.openNote', { number: reportNumber })
              : t('flatRate.openTheNote')}
          </Link>
        </>
      ) : null}
    </p>
  );
}

function Facts({ expense }: { readonly expense: Expense }) {
  const { t, i18n } = useTranslation('expenses');
  return (
    <dl className="grid grid-cols-1 gap-x-6 gap-y-3 @min-[620px]/shell:grid-cols-2">
      <Fact label={t('detail.fields.date')}>
        {expense.date ? formatLongDate(expense.date) : null}
      </Fact>
      <Fact label={t('detail.fields.total')}>
        {expense.total ? formatAmount(expense.total) : null}
      </Fact>
      <Fact label={t('detail.fields.supplier')}>
        {/* The name opens the supplier (U1); a name with no supplier in the list stays text. */}
        {expense.supplierId && expense.supplierName ? (
          <Link
            to={`/expenses/suppliers/${encodeURIComponent(expense.supplierId)}`}
            className="text-link font-semibold hover:underline"
          >
            {expense.supplierName}
          </Link>
        ) : (
          expense.supplierName
        )}
      </Fact>
      <TripFacts expense={expense} />
      <Fact label={t('detail.fields.supplierVat')}>{expense.supplierVat}</Fact>
      <Fact label={t('detail.fields.category')}>
        {expense.category ? categoryLabel(expense.category, i18n.resolvedLanguage) : null}
      </Fact>
      <Fact label={t('detail.fields.documentType')}>
        {expense.documentType
          ? [t(`detail.documentType.${expense.documentType}`), expense.documentNumber]
              .filter(Boolean)
              .join(' · ')
          : expense.documentNumber}
      </Fact>
      {/* Nothing was paid for an allowance line: it is owed to the person, so no «Pagata con». */}
      <Fact label={t('detail.fields.payer')}>
        {isAllowanceLine(expense) || !payment(expense, t) ? null : (
          <>
            {payment(expense, t)}
            <PaymentNote expense={expense} />
          </>
        )}
      </Fact>
      <Fact
        label={t(
          expense.paymentMethod === 'transfer' ? 'detail.fields.account' : 'detail.fields.card',
        )}
      >
        {expense.cardLabel}
      </Fact>
      <Fact label={t('detail.fields.vehicle')}>{expense.vehicleLabel}</Fact>
      <Fact label={t('detail.fields.nonTaxable')}>
        {expense.nonTaxable && expense.nonTaxable !== '0.00'
          ? formatAmount(expense.nonTaxable)
          : null}
      </Fact>
      <Fact label={t('detail.fields.businessReason')}>{expense.businessReason}</Fact>
      <Fact label={t('detail.fields.peopleInvolved')}>{expense.peopleInvolved}</Fact>
      <Fact label={t('detail.fields.place')}>{expense.place}</Fact>
      <Fact label={t('detail.fields.description')}>{expense.description}</Fact>
    </dl>
  );
}

function TaxLines({ lines }: { readonly lines: Expense['taxLines'] }) {
  const { t } = useTranslation('expenses');
  if (lines === undefined || lines.length === 0) return null;
  return (
    <div className="flex flex-col gap-2">
      <h3 className="text-sm font-semibold">{t('detail.taxLines.title')}</h3>
      <ul className="flex flex-col gap-1 text-sm">
        {lines.map((line, index) => (
          <li key={index} className="flex flex-wrap gap-x-2">
            <span className="font-semibold">{t(`detail.taxLines.type.${line.type}`)}</span>
            <span>{taxLineText(line, t)}</span>
            {line.rateUnclear ? (
              <span className="text-warning">{t('detail.taxLines.rateUnclear')}</span>
            ) : null}
          </li>
        ))}
      </ul>
    </div>
  );
}

function DocumentPane({
  expense,
  editPath,
}: {
  readonly expense: Expense;
  readonly editPath: string;
}) {
  const { t } = useTranslation('expenses');
  const document = expense.document ?? null;
  const documentStatus = expense.documentStatus;
  const shown = documentStatus === 'attached' && document !== null;
  const canAdd = (expense.can.edit || expense.can.replaceDocument) && !isAllowanceLine(expense);
  const replaced = document?.replacedCount ?? 0;

  return (
    <section aria-label={t('detail.document.title')} className="flex flex-col gap-3">
      <h3 className="text-sm font-semibold">{t('detail.document.title')}</h3>
      {shown ? (
        <>
          <DocumentPreview
            expenseId={expense.id}
            revision={`${document.sha256 ?? ''}${document.uploadedAt ?? ''}`}
          />
          {replaced > 0 ? (
            <p className="text-muted-foreground text-sm">
              {t('detail.document.replaced', { count: replaced })}
            </p>
          ) : null}
        </>
      ) : documentStatus === 'not-required' ? (
        // #951: nothing is missing, so no empty drop-zone box and no primary: one quiet line, and
        // adding a document anyway is the secondary choice.
        <div
          className="flex flex-wrap items-center gap-x-3 gap-y-2"
          data-testid="document-not-required"
        >
          <DocumentBadge status="not-required" />
          <p className="text-muted-foreground text-sm">{t('detail.document.notRequired')}</p>
          {canAdd ? (
            <Button asChild variant="outline">
              <Link to={editPath}>
                <FileWarning />
                {t('detail.document.add')}
              </Link>
            </Button>
          ) : null}
        </div>
      ) : (
        <div className="border-border bg-card flex flex-col items-start gap-3 rounded-xl border border-dashed p-4">
          <DocumentBadge status={documentStatus === 'attached' ? 'awaited' : documentStatus} />
          <p className="text-sm">
            <b>{t('detail.document.awaited')}</b>
            {expense.expectedDocumentNote ? (
              <>
                <br />«{expense.expectedDocumentNote}»
              </>
            ) : null}
          </p>
          {canAdd ? (
            <Button asChild>
              <Link to={editPath}>
                <Plus />
                {t('detail.document.add')}
              </Link>
            </Button>
          ) : null}
        </div>
      )}
    </section>
  );
}

function Fact({ label, children }: { readonly label: string; readonly children: ReactNode }) {
  if (children === null || children === undefined || children === '') return null;
  return (
    <div className="flex flex-col gap-0.5">
      <dt className="text-muted-foreground text-xs font-semibold">{label}</dt>
      <dd className="text-sm">{children}</dd>
    </div>
  );
}

type T = ReturnType<typeof useTranslation<'expenses'>>['t'];

function payment(expense: Expense, t: T): string | null {
  const parts: string[] = [];
  if (!expense.payer) return null;
  const way = paymentWayOf(expense.payer, expense.paymentMethod ?? null);
  parts.push(t(`capture.paymentWays.${way}`));
  if (way === 'director-advance' && expense.paymentMethod) {
    parts.push(t(`detail.paymentMethod.${expense.paymentMethod}`));
  }
  if (expense.traceablePayment) parts.push(t('detail.traceable'));
  return parts.length > 0 ? parts.join(', ') : null;
}

function taxLineText(line: NonNullable<Expense['taxLines']>[number], t: T): string {
  const amounts =
    line.base !== undefined
      ? `${formatAmount(line.base)} + ${formatAmount(line.tax)}`
      : formatAmount(line.tax);
  if (line.mode === 'rate' && line.rate !== undefined) return `${line.rate}% · ${amounts}`;
  if (line.mode === 'exempt') {
    return [t('detail.taxLines.exempt'), line.nature, amounts].filter(Boolean).join(' · ');
  }
  return amounts;
}

/** A 409 on the delete: the expense is no longer a draft. */
class NotADraft extends Error {}

/** A trip's own facts: the route, the km claimed and the rate frozen at capture (release 3). */
function TripFacts({ expense }: { readonly expense: Expense }) {
  const { t } = useTranslation('expenses');
  if (!expense.km) return null;
  return (
    <>
      <Fact label={t('detail.fields.route')}>{expense.route}</Fact>
      <Fact label={t('detail.fields.km')}>{formatKm(expense.km)} km</Fact>
      <Fact label={t('detail.fields.rate')}>
        {expense.aciRate ? `${formatRate(expense.aciRate)} ${t('mileage.rate.unit')}` : null}
      </Fact>
    </>
  );
}
